import { useMemo, useState } from 'react';
import { MenuItem, SelectChangeEvent } from '@mui/material';
import { CoreTypography } from '@youscience/core';

import { SortDirection } from '@common-types';
import { Members } from '@youscience/commonapp-service-common';

import { SortByOption } from '@pages/Scholarships/components/SortBy';

import useRecommenderWorkspaceStore from '@store/RecommenderWorkspaceStore';

import { SortByEnum, SortByValues } from '@interfaces/recommenderWorkspace';

import SortByIcon from '@images/sort.svg?react';

import SchoolCard, { SchoolCardSkeleton } from '../SchoolCard';

import { StyledSectionWrap, StyledSortByWrap, StyledSortSelect } from './CommonAppColleges.styles';
import { useApplicantFormsMetadata } from '@pages/StudentDetailsPage/hooks/useApplicantFormsMetadata';

const SORT_OPTIONS: SortByOption[] = [
  { value: `${SortByEnum.DEADLINE},${SortDirection.ASC}`, label: 'Application deadline, earliest-latest' },
  { value: `${SortByEnum.DEADLINE},${SortDirection.DESC}`, label: 'Application deadline, latest-earliest' },
  { value: `${SortByEnum.NAME},${SortDirection.ASC}`, label: 'Name: A to Z' },
  { value: `${SortByEnum.NAME},${SortDirection.DESC}`, label: 'Name: Z to A' },
];

interface CommonAppCollegesProps {
  studentName: string;
}

export const CommonAppColleges = (props: CommonAppCollegesProps) => {
  const { studentName } = props;

  const [openedSchoolCardId, setOpenedSchoolCardId] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<SortByValues>({ sort: SortByEnum.DEADLINE, sortDirection: SortDirection.ASC });

  const { applicant, isLoadingApplicant } = useRecommenderWorkspaceStore((store) => store);
  const { formsMetadata, isLoading: isLoadingMetadata } = useApplicantFormsMetadata();

  const members = useMemo(() => {
    const memberIdsForRecommender = Object.values(formsMetadata)
      .map((form) => form.members.map((member) => member.memberId))
      .flat();

    const memberHash = {} as Record<number, Members>;

    memberIdsForRecommender.forEach((memberId) => {
      const member = applicant?.members.find((member) => member.memberId === memberId);
      if (member) memberHash[memberId] = member;
    });

    return Object.values(memberHash);
  }, [formsMetadata, applicant]);

  const sortByName = (a: Members, b: Members) => {
    if (sortBy.sortDirection === SortDirection.ASC) {
      return (a.collegeName ?? '').localeCompare(b.collegeName ?? '');
    }

    return (b.collegeName ?? '').localeCompare(a.collegeName ?? '');
  };

  const sortDeadline = (a: Members, b: Members) => {
    if (sortBy.sortDirection === SortDirection.ASC) {
      return Date.parse(a.deadline as string) - Date.parse(b.deadline as string);
    }

    return Date.parse(b.deadline as string) - Date.parse(a.deadline as string);
  };

  const sortedSchools = useMemo(() => {
    if (sortBy.sort === SortByEnum.NAME) {
      return members.sort(sortByName);
    }

    if (sortBy.sort === SortByEnum.DEADLINE) {
      return members?.sort(sortDeadline);
    }

    return members;
  }, [sortBy, members]);

  const handleChangeSort = (event: SelectChangeEvent<unknown>) => {
    const [sort, sortDirection] = (event.target.value as string).split(',') as [SortByEnum, SortDirection];

    setSortBy({ sort, sortDirection });
  };

  return (
    <StyledSectionWrap>
      <StyledSortByWrap>
        <SortByIcon />

        <CoreTypography variant="h6" component="h5">
          Sort by:
        </CoreTypography>

        <StyledSortSelect
          data-testid="sort"
          value={`${sortBy.sort},${sortBy.sortDirection}`}
          onChange={handleChangeSort}
        >
          {SORT_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </StyledSortSelect>
      </StyledSortByWrap>

      {isLoadingMetadata || isLoadingApplicant
        ? [...Array(4)].map((_, index) => <SchoolCardSkeleton key={index} />)
        : sortedSchools?.map((member) => (
            <SchoolCard
              key={member.memberId}
              card={member}
              applicantFormsMetadata={formsMetadata}
              studentName={studentName}
              openedSchoolCardId={openedSchoolCardId}
              setOpenedSchoolCardId={setOpenedSchoolCardId}
            />
          ))}
    </StyledSectionWrap>
  );
};
